/**
 * <hero-tq-slideshow>
 *
 * Türkis-design hero slideshow custom element.
 * - Rotates child `.hero-slide` elements by toggling `.is-active`.
 * - Optional autoplay via `data-autoplay-ms="<number>"` attribute on the host
 *   element. Omitted/0 = no autoplay, manual navigation only.
 * - Optional indicator dots (`.hero-slide-dot[data-dot]`) inside the host;
 *   clicking a dot jumps to that slide and pauses autoplay until the user
 *   leaves the host.
 * - Respects prefers-reduced-motion: no autoplay if user opts out.
 * - Pause-on-hover for desktop, pause-on-touch-start for mobile.
 */
class HeroTqSlideshow extends HTMLElement {
  #slides = [];
  #dots = [];
  #active = 0;
  #timerId = 0;
  #intervalMs = 0;
  #paused = false;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('.hero-slide'));
    this.#dots = Array.from(this.querySelectorAll('.hero-slide-dot'));

    // Wire video mute toggles before any early-return below, so even
    // single-slide heros get a working mute button.
    const playButtons = this.querySelectorAll('[data-hero-play]');
    for (const btn of playButtons) {
      btn.addEventListener('click', this.#onPlayClick);
    }

    if (this.#slides.length === 0) return;
    if (this.#slides.length === 1) {
      // Single slide — make sure it is visible, no autoplay, no dots needed.
      this.#activate(0);
      return;
    }

    // Find current active slide (Liquid renders the first as .is-active).
    const initialActive = this.#slides.findIndex((s) => s.classList.contains('is-active'));
    this.#active = initialActive === -1 ? 0 : initialActive;
    this.#activate(this.#active);

    // Dot click handlers
    for (const dot of this.#dots) {
      dot.addEventListener('click', this.#onDotClick);
    }

    // Autoplay
    const ms = parseInt(this.dataset.autoplayMs ?? '0', 10);
    if (ms > 0 && !this.#prefersReducedMotion()) {
      this.#intervalMs = ms;
      this.#start();
      // Pause autoplay while the user is interacting with the hero
      this.addEventListener('mouseenter', this.#onPause);
      this.addEventListener('mouseleave', this.#onResume);
      this.addEventListener('focusin', this.#onPause);
      this.addEventListener('focusout', this.#onResume);
      this.addEventListener('touchstart', this.#onPause, { passive: true });
      this.addEventListener('touchend', this.#onResume, { passive: true });
    }
  }

  disconnectedCallback() {
    this.#stop();
    for (const dot of this.#dots) {
      dot.removeEventListener('click', this.#onDotClick);
    }
    this.removeEventListener('mouseenter', this.#onPause);
    this.removeEventListener('mouseleave', this.#onResume);
    this.removeEventListener('focusin', this.#onPause);
    this.removeEventListener('focusout', this.#onResume);
    this.removeEventListener('touchstart', this.#onPause);
    this.removeEventListener('touchend', this.#onResume);
  }

  #onDotClick = (event) => {
    const dot = event.currentTarget;
    const idx = parseInt(dot.dataset.dot ?? '0', 10);
    if (Number.isNaN(idx) || idx === this.#active) return;
    this.#activate(idx);
    // Restart the timer so the next auto-advance is a full interval away
    if (this.#intervalMs > 0) {
      this.#stop();
      if (!this.#paused) this.#start();
    }
  };

  #onPause = () => {
    this.#paused = true;
    this.#stop();
  };

  #onResume = () => {
    if (!this.#paused) return;
    this.#paused = false;
    if (this.#intervalMs > 0) this.#start();
  };

  #start() {
    this.#stop();
    this.#timerId = window.setInterval(() => this.#next(), this.#intervalMs);
  }

  #stop() {
    if (this.#timerId) {
      window.clearInterval(this.#timerId);
      this.#timerId = 0;
    }
  }

  #next() {
    const nextIdx = (this.#active + 1) % this.#slides.length;
    this.#activate(nextIdx);
  }

  #activate(idx) {
    for (let i = 0; i < this.#slides.length; i++) {
      const slide = this.#slides[i];
      const isActive = i === idx;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    }
    for (let i = 0; i < this.#dots.length; i++) {
      const dot = this.#dots[i];
      const isActive = i === idx;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', String(isActive));
    }
    this.#active = idx;
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Toggle mute state of the <video> inside the same .hero-image wrapper as
   * the clicked button. Reflects state via aria-pressed; CSS swaps the icon.
   */
  // User-initiated playback (UX workshop): the centered play button starts the
  // video and reveals native controls; the button then hides via `.is-playing`.
  #onPlayClick = (event) => {
    const btn = event.currentTarget;
    const wrap = btn.closest('.hero-image');
    const video = wrap?.querySelector('video');
    if (!video) return;
    video.controls = true;
    wrap.classList.add('is-playing');
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => { /* play() may reject if interrupted — fine */ });
    }
  };
}

if (!customElements.get('hero-tq-slideshow')) {
  customElements.define('hero-tq-slideshow', HeroTqSlideshow);
}

